import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../../i18n/useTranslation';
import { CombatGrid, bfsPath, canStepBetween } from '../../components/CombatGrid/CombatGrid';
import { useHubWebSocket } from '../../hooks/useHubWebSocket';
import { useParty } from '../../hooks/useParty';
import { useAuthStore } from '../../auth/authStore';
import { partyApi } from '../../auth/authApi';
import NpcDialogueModal from './NpcDialogueModal';
import MerchantModal from './MerchantModal';
import ForgeSection from './ForgeSection';
import PlayerToolbar from './PlayerToolbar';
import HubPartyDock from './HubPartyDock';
import PartyPortalModal, { type PartyPortalMember } from './PartyPortalModal';
import MissionPortalModal from './MissionPortalModal';
import { useToast, ToastContainer } from '../../components/Toast/Toast';
import styles from './HubMap.module.css';

interface HubMapProps {
  token: string;
  hubState?: any;
  character?: any;
  onStartBattle?: () => void;
  battling?: boolean;
}

// Raio de interatividade (em células) para conversar com NPCs — distância de
// Chebyshev (8 direções). 2 = o jogador pode falar de até 2 casas de distância.
const NPC_INTERACTION_RADIUS = 2;

// Duração aproximada de um passo no Hub — espelha `stepMs` de
// public/data/movement/character_movement.json (o CombatGrid usa esse valor
// para animar a caminhada célula-a-célula).
const HUB_STEP_MS = 170;

function isWithinInteractionRadius(
  px: number, py: number, nx: number, ny: number, radius = NPC_INTERACTION_RADIUS
): boolean {
  return Math.abs(px - nx) <= radius && Math.abs(py - ny) <= radius;
}

const BLACKSMITH_DIALOGUE_LINES = [
  'hub.npc.blacksmith.line1',
  'hub.npc.blacksmith.line2',
  'hub.npc.blacksmith.line3',
];

const GUARDIAN_DIALOGUE_LINES = [
  'hub.npc.guardian.line1',
  'hub.npc.guardian.line2',
];

const MERCHANT_DIALOGUE_LINES = [
  'hub.npc.merchant.line1',
  'hub.npc.merchant.line2',
];

export default function HubMap({ token, hubState: initialHubState, character, onStartBattle, battling }: HubMapProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const myUserId = user?.id ?? '';
  // Grupo/convites — mesmo poll do dashboard (ver useParty.ts), compartilhado
  // com o HubPartyDock (janela "👥") e usado aqui para detectar quando o
  // grupo inteiro chegou junto ao Portal do Guardião (ver allGathered abaixo).
  const partyState = useParty(token);
  const [hubState, setHubState] = useState<any>(initialHubState);
  const [loading, setLoading] = useState(!initialHubState);
  // Onde as janelas encaixadas começam — logo abaixo do painel "Sua Cidade".
  const cityPanelRef = useRef<HTMLDivElement>(null);
  const [dockTop, setDockTop] = useState(200);
  const [showNpcDialogue, setShowNpcDialogue] = useState(false);
  const [showBattleDialogue, setShowBattleDialogue] = useState(false);
  const [showPartyPortal, setShowPartyPortal] = useState(false);
  // Portal de Missão aberto: chave "X_Y" da célula clicada.
  const [missionPortalKey, setMissionPortalKey] = useState<string | null>(null);
  const [showMerchantDialogue, setShowMerchantDialogue] = useState(false);
  const [showMerchantShop, setShowMerchantShop] = useState(false);
  const { messages: toastMessages, toast, remove: removeToast } = useToast();

  // Convite recusado → o líder vê aqui mesmo estando com a janela de Grupo
  // fechada (o toast do PartySection só aparece com a janela aberta — ver
  // PartySection.tsx). HubMap fica sempre montado enquanto o jogador está no
  // Hub, então é o lugar certo pra isso não passar despercebido.
  useEffect(() => {
    if (partyState.notices.length === 0) return;
    for (const n of partyState.notices) {
      if (n.type === 'invite_declined') {
        toast.info(t('party.notice.declined' as any).replace('{name}', n.name));
      }
    }
    partyState.consumeNotices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partyState.notices]);
  // Conversa agendada: o jogador clicou num NPC longe → anda até ele e SÓ
  // ENTÃO abre o diálogo. Guardado num ref para poder cancelar (se o jogador
  // se mover no meio do caminho, clicar noutro lugar, etc.).
  const pendingTalk = useRef<ReturnType<typeof setTimeout> | null>(null);
  const clearPendingTalk = () => {
    if (pendingTalk.current) { clearTimeout(pendingTalk.current); pendingTalk.current = null; }
  };
  useEffect(() => clearPendingTalk, []);

  // Move o jogador de forma otimista e sincroniza com o servidor. Se o
  // backend rejeitar (limites do mapa / colisão — ver HubController::move),
  // a posição local é revertida para não dessincronizar do estado real.
  // O POST continua sendo quem valida e persiste; o WS só avisa os outros
  // jogadores em tempo real depois que o servidor aceitou (ver useHubWebSocket).
  const movePlayer = (x: number, y: number) => {
    const prevX = hubState.player.x;
    const prevY = hubState.player.y;

    setHubState((prev: any) => ({ ...prev, player: { ...prev.player, x, y } }));

    fetch('/api/user/hub/move', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ x, y })
    })
      .then(r => {
        if (r.ok) {
          sendMove(x, y);
        } else {
          setHubState((prev: any) => ({ ...prev, player: { ...prev.player, x: prevX, y: prevY } }));
        }
      })
      .catch(() => {
        setHubState((prev: any) => ({ ...prev, player: { ...prev.player, x: prevX, y: prevY } }));
      });
  };

  const refreshHubState = () => {
    fetch('/api/user/hub/state', {
      headers: { Authorization: `Bearer ${token}` },
      cache: 'no-store',
    })
      .then(r => (r.ok ? r.json() : null))
      .then(data => { if (data && data.config) setHubState(data); })
      .catch(() => {});
  };

  useEffect(() => {
    if (initialHubState) return;
    fetch('/api/user/hub/state', {
      headers: { Authorization: `Bearer ${token}` },
      cache: 'no-store'
    })
      .then(r => r.json())
      .then(data => {
        setHubState(data);
        setLoading(false);
      });
  }, [token, initialHubState]);

  // Acompanha a base do painel "Sua Cidade" — as janelas encaixam logo abaixo.
  useEffect(() => {
    const measure = () => {
      const r = cityPanelRef.current?.getBoundingClientRect();
      if (r) setDockTop(Math.round(r.bottom + 8));
    };
    measure();
    const ro = new ResizeObserver(measure);
    if (cityPanelRef.current) ro.observe(cityPanelRef.current);
    window.addEventListener('resize', measure);
    // remede também no próximo frame (layout já aplicado)
    const raf = requestAnimationFrame(measure);
    return () => { ro.disconnect(); window.removeEventListener('resize', measure); cancelAnimationFrame(raf); };
  }, [loading, battling, hubState?.config]);

  // Poção usada (PlayerToolbar → InventorySection) → atualiza a energia no overlay.
  useEffect(() => {
    const onEnergy = (e: Event) => {
      const val = (e as CustomEvent).detail;
      if (typeof val === 'number') {
        setHubState((prev: any) => prev ? { ...prev, player: { ...prev.player, energy: val } } : prev);
      }
    };
    window.addEventListener('energy-changed', onEnergy);
    return () => window.removeEventListener('energy-changed', onEnergy);
  }, []);

  // Mapa renderizável mais recente (declarado abaixo) — lido pelo teclado.
  const combatMapRef = useRef<unknown>(null);

  // Teclado WASD / Setas
  useEffect(() => {
    if (!hubState) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      let dx = 0; let dy = 0;
      if (e.key === 'w' || e.key === 'ArrowUp') { dy = -1; }
      else if (e.key === 's' || e.key === 'ArrowDown') { dy = 1; }
      else if (e.key === 'a' || e.key === 'ArrowLeft') { dx = -1; }
      else if (e.key === 'd' || e.key === 'ArrowRight') { dx = 1; }

      if (dx !== 0 || dy !== 0) {
        // Enviar para o servidor (Debounce seria recomendado em produção)
        const newX = hubState.player.x + dx;
        const newY = hubState.player.y + dy;

        // Limites do mapa
        if (newX < 0 || newY < 0 || newX >= hubState.config.width || newY >= hubState.config.height) return;

        // Validação de colisão otimista no frontend
        const key = `${newX}_${newY}`;
        const cell = hubState.config.cells[key] || {};
        const cellTypeLower = (cell.type || '').toLowerCase();
        const obstacles = ['obstacle', 'water', 'lake', 'tree', 'npc_blacksmith', 'npc_merchant', 'npc_farmer', 'battle_point', 'mission_portal', 'full_obstacle'];
        if (obstacles.includes(cellTypeLower)) return; // Bloqueio
        // Nível diferente só por Rampa/Escada/Corda.
        if (combatMapRef.current && !canStepBetween(combatMapRef.current as any, hubState.player.x, hubState.player.y, newX, newY)) return;

        clearPendingTalk();   // moveu manualmente → cancela conversa agendada
        movePlayer(newX, newY);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [hubState, token]);

  // Memoizado na CONFIG do mapa (não na posição do jogador) — recriar esse
  // array de ~centenas de células a cada passo travava a animação.
  const combatMap = useMemo(() => {
    const cfg = hubState?.config;
    if (!cfg) return null;
    return {
      width: cfg.width,
      height: cfg.height,
      north: cfg.north ?? 0,
      cells: Array.from({ length: cfg.height }, (_, y) =>
        Array.from({ length: cfg.width }, (_, x) => {
          const raw = cfg.cells[`${x}_${y}`];
          const tp = raw?.type;
          let cType = 'Walkable';
          if (tp === 'obstacle') cType = 'Full_Obstacle';
          if (tp === 'water') cType = 'Lake';
          if (tp === 'tree') cType = 'tree';
          // Portal de Missão: desenhado pelo ramo do portal, mas com visual
          // próprio (portal_style) e sem o Guardião, que só nasce na célula
          // battle_point — ver findCell abaixo.
          if (tp === 'battle_point' || tp === 'mission_portal') cType = 'battle_point';
          if (tp === 'npc_blacksmith') cType = 'npc_blacksmith';
          if (tp === 'npc_merchant' || tp === 'npc_farmer') cType = 'npc_merchant';
          // 'dirt' (Chão Batido) é caminhável — só muda o revestimento do piso.
          return {
            x, y, type: cType as any,
            ...(tp === 'dirt' ? { floor: 'dirt' as const } : {}),
            ...(tp === 'mission_portal' ? { portal_style: 'mission' as const, portal_label: t('mission.portal.sign') } : {}),
            // Relevo: nível 0/1/2 + Via de Subida (Rampa/Escada/Corda) — mesma
            // regra de passagem entre níveis do combate (canStepBetween).
            // Só terreno caminhável/Chão Batido tem altura (dados antigos
            // com lago/obstáculo elevado são desenhados no chão).
            elevation: !tp || tp === 'walkable' || tp === 'dirt' ? Number(raw?.elevation ?? 0) : 0,
            climb_connection: raw?.climb_connection ?? null,
          };
        }),
      ),
    };
  }, [hubState?.config, t]);
  combatMapRef.current = combatMap;

  // NPCs data-driven (systems/movement): a partir da célula que os posiciona
  // no mapa do Hub, eles executam patrulha + ação (forjar / conjurar / farmar)
  // 100% no cliente — o servidor só valida a colisão do jogador.
  const findCell = (type: string): { x: number; y: number } | null => {
    if (!hubState) return null;
    for (const [key, val] of Object.entries<any>(hubState.config.cells)) {
      if (val?.type === type) {
        const [cx, cy] = key.split('_').map(Number);
        return { x: cx, y: cy };
      }
    }
    return null;
  };
  const blacksmithPos = findCell('npc_blacksmith');
  const guardianPos = findCell('battle_point');
  const merchantPos = findCell('npc_merchant') ?? findCell('npc_farmer');
  const ambientNpcs = useMemo(() => [
    ...(blacksmithPos ? [{ entityId: 'blacksmith', profileId: 'blacksmith_forge', startPos: blacksmithPos }] : []),
    ...(guardianPos ? [{ entityId: 'guardian', profileId: 'portal_guardian', startPos: guardianPos }] : []),
    ...(merchantPos ? [{ entityId: 'merchant', profileId: 'merchant_stall', startPos: merchantPos }] : []),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [hubState?.config?.cells]);
  const npcNames = useMemo<Record<string, string>>(() => ({
    blacksmith: t('hub.npc.blacksmith.name' as any),
    guardian: t('hub.npc.guardian.name' as any),
    merchant: t('hub.npc.merchant.name' as any),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [t]);

  // Clicou num NPC: se já está perto (raio de interação), abre o diálogo na
  // hora. Se está longe, ANDA até a célula caminhável mais próxima do NPC e só
  // então abre — mesma sensação de RPG clássico. `unreachableHint` é mostrado
  // se não houver caminho.
  const approachAndTalk = (
    npcX: number, npcY: number,
    openDialogue: () => void,
    unreachableHintKey: string,
  ) => {
    clearPendingTalk();
    const px = hubState.player.x, py = hubState.player.y;

    if (isWithinInteractionRadius(px, py, npcX, npcY)) {
      openDialogue();
      return;
    }
    if (!combatMap) return;

    // Melhor célula caminhável adjacente ao NPC (ortogonais antes das diagonais)
    // com o menor caminho a partir do jogador.
    const neighbours = [
      [0, 1], [0, -1], [1, 0], [-1, 0], [1, 1], [1, -1], [-1, 1], [-1, -1],
    ];
    let best: { path: { x: number; y: number }[] } | null = null;
    for (const [ox, oy] of neighbours) {
      const tx = npcX + ox, ty = npcY + oy;
      if (tx < 0 || ty < 0 || tx >= combatMap.width || ty >= combatMap.height) continue;
      const path = bfsPath(px, py, tx, ty, combatMap as any, new Set());
      if (path && path.length >= 1 && (!best || path.length < best.path.length)) {
        best = { path };
      }
    }

    if (!best) {
      toast.info(t(unreachableHintKey as any));
      return;
    }

    const dest = best.path[best.path.length - 1];
    movePlayer(dest.x, dest.y);                          // CombatGrid anima a caminhada
    const walkMs = Math.max(0, best.path.length - 1) * HUB_STEP_MS + 320;
    pendingTalk.current = setTimeout(() => {
      pendingTalk.current = null;
      openDialogue();
    }, walkMs);
  };

  // Outros jogadores online e sem batalha ativa, visíveis no Hub em tempo
  // real via WebSocket — "conectado ao Hub" É a definição de "sem batalha":
  // sair pra uma luta desmonta este componente, que fecha o socket, e todo
  // mundo mais vê o PLAYER_LEFT na hora (ver useHubWebSocket.ts).
  const { otherPlayers, sendMove } = useHubWebSocket({ token });

  const px = hubState?.player?.x, py = hubState?.player?.y, pe = hubState?.player?.energy;
  const entities = useMemo<any>(() => {
    if (px == null || py == null) return {};
    const out: any = {
      player_1: {
        id: 'player_1', name: character?.name || 'Player', team: 'player',
        position_x: px, position_y: py,
        // hp > 0 mantém o personagem visível (o CombatGrid filtra entidades
        // com hp <= 0). hp_max: 0 faz o CombatGrid NÃO desenhar barra sob o
        // nome — no Hub a energia aparece só na barra do topo do mapa.
        hp: 1, hp_max: 0,
        mp: 0, mp_max: 0, ap: 0, ap_max: 0,
        sprite: character ? character.class : undefined,
        sprite_config: { cols: 3, rows: 8, scale: 1.2, offsetX: 0, offsetY: 0 },
      },
    };
    for (const p of otherPlayers) {
      out[`other_${p.id}`] = {
        id: `other_${p.id}`, name: p.name, team: 'player',
        position_x: p.x, position_y: p.y,
        hp: 1, hp_max: 0,
        mp: 0, mp_max: 0, ap: 0, ap_max: 0,
        sprite: p.class,
        sprite_config: { cols: 3, rows: 8, scale: 1.2, offsetX: 0, offsetY: 0 },
      };
    }
    return out;
  }, [px, py, pe, character?.class, character?.name, otherPlayers]);

  // ── Portal do Grupo ──────────────────────────────────────────────────────
  // Ao contrário da batalha solo (confirma no diálogo e entra na hora), um
  // grupo (>1 membro) precisa se reunir FISICAMENTE junto ao Guardião — a
  // batalha só começa quando a posição de TODOS (a minha + as dos outros via
  // WS) cair dentro do raio de interação do portal.
  //
  // Party guarda userId; o Hub WS guarda characterId (é o que anima no mapa)
  // — leader/members trazem `character.id` do backend (ver
  // PartyController::memberProfile), que é a ponte entre os dois.
  const partyRoster = useMemo(() => {
    const p = partyState.party;
    if (!p) return [];
    return [
      { userId: p.leader_id, name: p.leader.character?.name ?? p.leader.email, isLeader: true, characterId: p.leader.character?.id ?? null },
      ...p.members.map(m => ({ userId: m.user_id, name: m.character?.name ?? m.email, isLeader: false, characterId: m.character?.id ?? null })),
    ];
  }, [partyState.party]);

  const portalMembers = useMemo<PartyPortalMember[]>(() => {
    if (!guardianPos || partyRoster.length === 0) return [];
    return partyRoster.map(r => {
      const pos =
        character && r.characterId === character.id
          ? (px != null && py != null ? { x: px, y: py } : null)
          : (r.characterId ? otherPlayers.find(o => o.id === r.characterId) ?? null : null);

      const status: PartyPortalMember['status'] = !pos
        ? 'offline'
        : isWithinInteractionRadius(pos.x, pos.y, guardianPos.x, guardianPos.y)
          ? 'here'
          : 'enroute';

      return { userId: r.userId, name: r.name, isLeader: r.isLeader, isMe: r.userId === myUserId, status };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partyRoster, guardianPos, otherPlayers, px, py, character?.id, myUserId]);

  const allGathered =
    !!partyState.party &&
    partyState.party.size > 1 &&
    partyState.party.status === 'waiting' &&
    portalMembers.length > 0 &&
    portalMembers.every(m => m.status === 'here');

  // Só o líder dispara — os outros membros entram sozinhos assim que o poll
  // do useParty (partyState) detectar status=in_combat (mesmo mecanismo do
  // botão "Iniciar" manual do dashboard — ver useParty.ts).
  const startingPartyRef = useRef(false);
  useEffect(() => {
    if (!allGathered) { startingPartyRef.current = false; return; }
    if (partyState.party?.leader_id !== myUserId) return;
    if (startingPartyRef.current) return;
    startingPartyRef.current = true;

    partyApi.start(token)
      .then(res => {
        const myEntityId = res.entity_map[myUserId];
        if (myEntityId) {
          partyState.markNavigated(res.combat_id);
          navigate(`/play?combat_id=${res.combat_id}&entity_id=${myEntityId}`);
        }
      })
      .catch(() => { startingPartyRef.current = false; });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allGathered, myUserId, token]);

  // Tela de espera SÓ depois de todos os hooks: um return antecipado antes
  // deles fazia o React chamar menos hooks quando `battling` virava true
  // (erro #300) — a árvore inteira caía e a tela ficava preta ao entrar na
  // batalha, até dar F5.
  if (loading || battling) return (
    <div className={styles.hubContainer} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ textAlign: 'center', color: '#fff' }}>
        <div className="spinner" style={{ margin: '0 auto 10px' }} />
        {battling ? 'Preparando Batalha...' : t('hub.loading' as any)}
      </div>
    </div>
  );

  return (
    <div className={styles.hubContainer}>
      <div ref={cityPanelRef} className={styles.hubOverlayUI} style={{ zIndex: 10 }}>
        <h3>{t('hub.title' as any)}</h3>
        <p>{t('hub.controls' as any)}</p>

        {/* Atalhos pequenos logo abaixo do painel — Equipamento, Mochila e
            Ficha. Forja e Mercado já têm NPC no mapa. As janelas abrem
            ENCAIXADAS logo abaixo do painel, sem escurecer o fundo. */}
        {character && (
          <div className={styles.hubQuickBar}>
            <PlayerToolbar
              token={token}
              character={character}
              classColor={character.class_data?.color}
              onCharacterUpdate={() => {}}
              variant="compact"
              dockTop={dockTop}
            />
            <HubPartyDock token={token} dockTop={dockTop} partyState={partyState} />
          </div>
        )}
      </div>
      {/* `display:flex` é OBRIGATÓRIO aqui: `CombatGrid`'s `.wrapper` usa
          `flex:1` pra preencher a altura disponível — sem um pai flex, esse
          `flex:1` não tem efeito nenhum, o wrapper encolhe pro tamanho do
          conteúdo, e o canvas cai pro tamanho intrínseco padrão do elemento
          `<canvas>` (300×150px) — exatamente o "mapa muito pequeno" relatado. */}
      <div style={{ position: 'absolute', inset: 0, zIndex: 0, display: 'flex' }}>
        {combatMap && (
          <CombatGrid
            map={combatMap as any}
            entities={entities}
            myEntityId="player_1"
            highlights={[]}
            cellHover={null}
            rotationMode="snap90"
            npcSprites={hubState.npcs}
            npcNames={npcNames}
            ambientNpcs={ambientNpcs}
            onCellClick={(x, y) => {
              const key = `${x}_${y}`;
              const cell = hubState.config.cells[key] || {};

              if (cell.type === 'npc_blacksmith') {
                // Inicia o diálogo com o Ferreiro (a Forja só abre ao
                // concluir/pular a conversa). Anda até ele se estiver longe.
                approachAndTalk(x, y, () => setShowNpcDialogue(true), 'hub.npc.tooHint');
                return;
              }

              if (cell.type === 'npc_merchant' || cell.type === 'npc_farmer') {
                approachAndTalk(x, y, () => setShowMerchantDialogue(true), 'hub.npc.tooHint');
                return;
              }

              if (cell.type === 'mission_portal') {
                approachAndTalk(x, y, () => setMissionPortalKey(`${x}_${y}`), 'hub.npc.guardian.tooHint');
                return;
              }

              if (cell.type === 'battle_point') {
                // Em grupo (>1 membro): o Portal exige todos reunidos — mostra
                // o painel de status em vez do diálogo solo (ver allGathered
                // acima, que dispara a batalha sozinho quando todos chegam).
                // Sozinho: o Guardião pergunta e a batalha começa ao confirmar.
                if (partyState.party && partyState.party.size > 1) {
                  approachAndTalk(x, y, () => setShowPartyPortal(true), 'hub.npc.guardian.tooHint');
                } else {
                  approachAndTalk(x, y, () => setShowBattleDialogue(true), 'hub.npc.guardian.tooHint');
                }
                return;
              }

              const cellTypeLower = (cell.type || '').toLowerCase();
              const obstacles = ['obstacle', 'water', 'lake', 'tree', 'npc_blacksmith', 'npc_merchant', 'npc_farmer', 'battle_point', 'mission_portal', 'full_obstacle'];
              if (obstacles.includes(cellTypeLower)) return;
              // Destino em outro nível sem caminho (Rampa/Escada/Corda) → não anda.
              if (combatMap && !bfsPath(hubState.player.x, hubState.player.y, x, y, combatMap as any, new Set())) return;

              clearPendingTalk();   // clicou pra andar noutro lugar → cancela conversa agendada
              movePlayer(x, y);
            }}
            onCellHover={() => {}}
            onMouseLeave={() => {}}
          />
        )}
      </div>

      {/* Janela da Forja (sem botão próprio) — abre pelo evento 'open-forge'
          que o diálogo do Ferreiro dispara. Precisa estar montada no Hub: a
          barra compacta (PlayerToolbar) não renderiza a Forja. */}
      {token && <ForgeSection token={token} hideButton />}

      {showNpcDialogue && (
        <NpcDialogueModal
          npcNameKey="hub.npc.blacksmith.name"
          npcIcon="🧔"
          lineKeys={BLACKSMITH_DIALOGUE_LINES}
          onFinish={() => {
            setShowNpcDialogue(false);
            // ForgeSection (renderizado acima com hideButton) escuta este mesmo
            // evento global — é o componente reutilizável idêntico ao usado
            // no dashboard fora do HUB.
            window.dispatchEvent(new Event('open-forge'));
          }}
          onClose={() => setShowNpcDialogue(false)}
        />
      )}

      {missionPortalKey && (
        <MissionPortalModal token={token} portalKey={missionPortalKey} onClose={() => setMissionPortalKey(null)} />
      )}

      {showBattleDialogue && (
        <NpcDialogueModal
          npcNameKey="hub.npc.guardian.name"
          npcIcon="🧙"
          lineKeys={GUARDIAN_DIALOGUE_LINES}
          finishIcon="⚔"
          finishLabel={t('hub.npc.guardian.enter' as any)}
          onFinish={() => {
            setShowBattleDialogue(false);
            onStartBattle?.();
          }}
          onClose={() => setShowBattleDialogue(false)}
        />
      )}

      {showMerchantDialogue && (
        <NpcDialogueModal
          npcNameKey="hub.npc.merchant.name"
          npcIcon="🛒"
          lineKeys={MERCHANT_DIALOGUE_LINES}
          finishIcon="🛒"
          finishLabel={t('merchant.open' as any)}
          onFinish={() => { setShowMerchantDialogue(false); setShowMerchantShop(true); }}
          onClose={() => setShowMerchantDialogue(false)}
        />
      )}

      {showMerchantShop && (
        <MerchantModal
          token={token}
          onClose={() => setShowMerchantShop(false)}
          onChanged={refreshHubState}
        />
      )}

      {showPartyPortal && (
        <PartyPortalModal
          members={portalMembers}
          allGathered={allGathered}
          onClose={() => setShowPartyPortal(false)}
        />
      )}

      <ToastContainer messages={toastMessages} onRemove={removeToast} />
    </div>
  );
}
